export const SERVICES = [
  {
    slug: "registration-system",
    icon: "registration",
    title: "Registration System",
    tagline: "Online registration with international payment support and an admin panel to manage participant data, categories, and reports.",
    description:
      "Our Registration System provides a complete participant onboarding experience — from the first sign-up form to BIB confirmation. Built for organizers who manage hundreds or thousands of registrants, it handles multi-category event structures, custom data fields, and automated communication workflows. The admin panel gives your team full visibility over every registration record, waitlist, payment status, and category assignment in real time.",
    features: [
      {
        title: "Multi-step Registration Flow",
        description:
          "Configurable registration forms with category selection, custom fields, waiver signing, and instant email confirmation for every participant.",
      },
      {
        title: "International Payment Support",
        description:
          "Accept payments from local and international participants with support for multiple payment gateways and currencies.",
      },
      {
        title: "Category & Quota Management",
        description:
          "Define unlimited categories with independent quotas, pricing tiers, and early-bird cut-offs. Waitlists are handled automatically.",
      },
      {
        title: "Admin Dashboard",
        description:
          "Real-time visibility over all registrations, payment statuses, category breakdowns, and exportable participant data.",
      },
      {
        title: "Automated Communication",
        description:
          "Triggered email notifications for registration confirmation, payment reminders, racepack pickup schedules, and event day briefings.",
      },
      {
        title: "Reports & Data Export",
        description:
          "One-click export to PDF, Excel, and CSV for federation submissions, sponsor reports, and on-ground operations.",
      },
    ],
    cta: "Set up registration for your next event",
  },
  {
    slug: "racepack-collection-system",
    icon: "racepack",
    title: "Racepack Collection System",
    tagline: "QR-based check-in system for verifying participants and distributing race kits, with real-time collection tracking via admin panel.",
    description:
      "The Racepack Collection System eliminates long queues and manual verification at expo or kit collection days. Each registered participant receives a unique QR code that is scanned at the collection point to instantly verify their identity, confirm their category, and mark their kit as collected. Organizers monitor collection progress live from the admin panel — seeing exactly who has collected, who hasn't, and flagging any discrepancies in real time.",
    features: [
      {
        title: "QR-based Participant Verification",
        description:
          "Each participant receives a unique QR code upon registration. Staff scan it at collection to instantly confirm identity and eligibility.",
      },
      {
        title: "Real-time Collection Tracking",
        description:
          "Live dashboard shows collection progress by category — organizers know at a glance who has picked up their kit and who hasn't.",
      },
      {
        title: "BIB & Kit Assignment",
        description:
          "Automatically link BIB numbers and kit sizes to each participant. Staff see the correct allocation on screen at the moment of scan.",
      },
      {
        title: "Multi-station Support",
        description:
          "Deploy multiple collection stations simultaneously. All data syncs centrally so the admin view is always accurate and up to date.",
      },
      {
        title: "Exception Flagging",
        description:
          "Duplicate scans, unregistered participants, and missing data are flagged immediately, preventing kit distribution errors.",
      },
      {
        title: "Post-collection Reports",
        description:
          "Detailed collection summaries exported for operations records, including uncollected kits and late pickup statistics.",
      },
    ],
    cta: "Streamline your racepack collection",
  },
  {
    slug: "timing-system",
    icon: "timing",
    title: "Precision & Live Timing System",
    tagline: "Chip-based timing that goes live during the event, with real-time results, split times, and public leaderboard accessible by all.",
    liveUrl: "https://timing.vionixasia.com",
    liveTitle: "See It Live",
    liveSubtitle: "The live timing platform — every event, result, and leaderboard, published in real time.",
    description:
      "Our Precision Timing System uses RFID chip technology deployed at the start, finish, and every checkpoint on the course. Results are computed and published to the public leaderboard within seconds of each detection. Organizers, athletes, and spectators all see the same accurate, real-time data — making every finish line moment transparent and verifiable.",
    features: [
      {
        title: "RFID Chip-based Accuracy",
        description:
          "Millisecond-precise chip detection at every reader point, with redundant readers ensuring no participant is missed.",
      },
      {
        title: "Real-time Public Leaderboard",
        description:
          "Results publish live to a public URL the moment each chip is detected. No manual updates needed.",
      },
      {
        title: "Split Time Detection",
        description:
          "Deploy checkpoints at multiple points on the course to capture gun time, net time, and split times per participant.",
      },
      {
        title: "Multi-discipline Support",
        description:
          "Configured for trail running, road races, triathlon, cycling, and custom event formats with per-category ranking rules.",
      },
      {
        title: "DNF / DSQ Management",
        description:
          "Identify missed checkpoints, disqualifications, and did-not-finish records automatically with override capability for race officials.",
      },
      {
        title: "Export & Official Reports",
        description:
          "Generate official results in PDF, Excel, and CSV format for federations, podium ceremonies, and archive records.",
      },
    ],
    cta: "Deploy precision timing at your race",
  },
  {
    slug: "results-certificate-system",
    icon: "results",
    title: "Results & Certificate System",
    tagline: "Instant digital certificate delivery and public results page for all categories, automatically generated from live timing data.",
    description:
      "The Results & Certificate System connects directly to the timing platform to deliver a public results page and personalized finisher certificates the moment the race ends. Participants receive their certificate via the registration portal — branded, accurate, and ready to share. Organizers get a clean, filterable results page that works for multi-category events of any complexity.",
    features: [
      {
        title: "Public Results Page",
        description:
          "A clean, filterable public-facing results page updated live from timing data. Accessible by participants and spectators without login.",
      },
      {
        title: "Automated Certificate Generation",
        description:
          "Every finisher automatically receives a personalized digital certificate with their name, BIB, category, finish time, and event branding.",
      },
      {
        title: "Multi-category Support",
        description:
          "Results are independently ranked per category — overall, gender, and age group breakdowns are all generated automatically.",
      },
      {
        title: "Instant Participant Access",
        description:
          "Participants access their certificate and results directly from their registration portal account — no manual distribution required.",
      },
      {
        title: "Top 3 Podium Display",
        description:
          "Highlighted podium section for each category, suitable for projection at award ceremonies and social media sharing.",
      },
      {
        title: "Offline Export",
        description:
          "Full results and certificates available for bulk download in PDF and Excel format for archive, federation, and sponsor use.",
      },
    ],
    cta: "Automate your results and certificates",
  },
  {
    slug: "ai-recognition-gallery",
    icon: "gallery",
    title: "AI-Powered Recognition Gallery",
    tagline: "A gallery of every race moment, made searchable by AI — runners find their photos instantly without scrolling through thousands of images.",
    description:
      "Our AI-Powered Recognition Gallery turns event photography into a seamless participant experience. Thousands of race photos are uploaded and processed by our facial recognition engine. Each participant is matched to their photos automatically. They log in to their profile, see only their photos, and download or share them directly — without scrolling through an entire gallery. Organizers upload, we handle the rest.",
    features: [
      {
        title: "Facial Recognition Matching",
        description:
          "AI automatically identifies each participant across thousands of photos and delivers only their matched images to their profile.",
      },
      {
        title: "Zero Manual Sorting",
        description:
          "Organizers upload photos in bulk. The AI handles all categorization and participant matching without any manual tagging.",
      },
      {
        title: "Instant Participant Access",
        description:
          "Participants see their matched photos immediately after uploading — no waiting, no searching.",
      },
      {
        title: "High-resolution Downloads",
        description:
          "Full-resolution photo downloads available to participants directly from their gallery — watermarked or clean depending on organizer preference.",
      },
      {
        title: "Shareable Gallery Links",
        description:
          "Each participant can share their personal race gallery via a public link — ideal for social media engagement.",
      },
      {
        title: "Bulk Photographer Upload",
        description:
          "Support for multi-photographer events with batch upload tools and organized folder structure by checkpoint or time.",
      },
    ],
    cta: "Deliver photo memories your participants will love",
  },
  {
    slug: "live-tracking-map",
    icon: "tracking",
    title: "Live Race Tracking Map",
    tagline: "Real-time course map showing runner positions, checkpoints, and aid stations for spectators and organizers during the event.",
    liveContent: true,
    description:
      "The Live Race Tracking Map puts every participant on an interactive course map in real time. Powered by GPS devices or smartbands, each runner's position is updated continuously so spectators can follow their athlete, organizers can monitor course progress, and safety teams can identify participants who need assistance. Post-race, the full tracking data is available for replay and analysis.",
    features: [
      {
        title: "Real-time GPS Positioning",
        description:
          "Each participant's location updates continuously on the map. Spectators and organizers see positions live throughout the race.",
      },
      {
        title: "Interactive Course Map",
        description:
          "Public-facing map with course route, checkpoints, aid stations, and participant positions — accessible from any browser.",
      },
      {
        title: "GPS Device & Smartband Integration",
        description:
          "Compatible with a range of GPS trackers and wearables. We support setup, distribution, and collection on race day.",
      },
      {
        title: "Checkpoint Alerts",
        description:
          "Automated notifications when a tracked participant passes a checkpoint — sent to registered spectator contacts.",
      },
      {
        title: "Safety Monitoring",
        description:
          "Identify stationary or off-route participants quickly. Organizers can flag and dispatch support directly from the admin view.",
      },
      {
        title: "Post-race Replay",
        description:
          "Replay the entire race from any point in time. Useful for analysis, highlight reels, and post-event reviews.",
      },
    ],
    cta: "Put every runner on the map",
  },
  {
    slug: "race-concept-design",
    icon: "concept",
    title: "Race Concept & Experience Design",
    tagline: "Creative direction and experience design for your race — from brand identity to course theming and participant journey.",
    description:
      "A great race is more than a route and a finish line. Our Race Concept & Experience Design service helps organizers build events with a clear identity, memorable atmosphere, and a participant journey that earns loyalty year after year. We work with you from initial concept through visual branding, course narrative, checkpoint theming, and finish line experience — ensuring every touchpoint reflects your event's character.",
    features: [
      {
        title: "Race Identity & Branding",
        description:
          "Logo, color palette, typography, and visual language developed to give your race a distinct, recognizable identity.",
      },
      {
        title: "Course Narrative & Theming",
        description:
          "Define the story your course tells — from start zone ambiance to checkpoint theming and finish line design.",
      },
      {
        title: "Participant Journey Mapping",
        description:
          "Map every participant touchpoint from registration email to medal moment — identifying opportunities to delight and surprise.",
      },
      {
        title: "Print & Digital Collateral",
        description:
          "Race bib design, banners, signage, social media templates, and participant guides designed to a consistent standard.",
      },
      {
        title: "Merchandise Concept",
        description:
          "T-shirt, medal, and finisher item concepts aligned with the race identity — designed to be collectible and worth keeping.",
      },
      {
        title: "Sponsor Integration Design",
        description:
          "Thoughtful sponsor placement across all collateral, the course, and digital assets that adds value without detracting from the experience.",
      },
    ],
    cta: "Build a race with a real identity",
  },
  {
    slug: "event-fulfilment",
    icon: "fulfilment",
    title: "Event Fulfilment",
    tagline: "End-to-end on-ground execution covering logistics, marshalling, and volunteer management throughout the event.",
    description:
      "Event Fulfilment is our operational backbone service — putting experienced people and processes on the ground so your race runs exactly as planned. From pre-event logistics coordination and marshal briefings to real-time course management and post-event teardown, our team integrates seamlessly with yours. We handle the complexity so you can focus on the experience.",
    features: [
      {
        title: "Logistics Coordination",
        description:
          "Equipment transportation, setup schedules, and venue coordination managed end-to-end from pre-event planning through race day.",
      },
      {
        title: "Marshal Deployment & Briefing",
        description:
          "Trained marshals positioned at every critical course point with clear briefing materials and real-time communication.",
      },
      {
        title: "Volunteer Management",
        description:
          "Volunteer recruitment, role assignment, scheduling, and on-day coordination handled through a structured management system.",
      },
      {
        title: "Course Setup & Signage",
        description:
          "Physical course marking, directional signage, aid station setup, and safety barrier installation by our ground crew.",
      },
      {
        title: "Real-time Operations Control",
        description:
          "Dedicated operations lead monitoring all course sectors during the event with radio coordination and incident response protocols.",
      },
      {
        title: "Post-event Teardown",
        description:
          "Full course teardown, equipment collection, and venue handback managed by our team so organizers can focus on the celebration.",
      },
    ],
    cta: "Let us handle the ground operation",
  },
];

export const getServiceBySlug = (slug) =>
  SERVICES.find((s) => s.slug === slug) || null;
